import { ProtectedRoute } from '@aibees/elephant/components/protected-route';
import { ROUTES as R } from '@aibees/elephant/router';
import { AuthConsumer } from '@aibees/shared/auth';
import {
  FullScreenLoading,
  LoadingScreen,
  PostHogIdentify,
  SplashScreen,
} from '@aibees/shared/components';
import { FullScreenLoadingProvider } from '@aibees/shared/context';
import { RealmAccessRole } from '@aibees/shared/definition';
import { SocketProvider } from '@aibees/shared/socket';
import { View404 } from '@aibees/shared/views/error';
import { WorkspaceProvider } from '@aibees/shared/workspace';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import {
  InsufficientCreditsDialog,
  SearchLimitAlertDialog,
} from '../components';
import { ExportSearchLoader } from '../components/export-search-loader';
import { RedirectToSignup } from '../components/redirect-to-signup';
import { SOCKET_BASE_URL } from '../constants';
import { ActionButtonContextProvider } from '../context/action-button-context';
import { SearchParamsSourceProvider } from '../context/search-params-source-provider';
import { SearchParamsUrlSync } from '../context/search-params-url-sync';
import { SearchTableScopeRouteProvider } from '../context/search-table-scope';
import { TableActionProvider } from '../context/table-action/provider';

const CompanyPageLayout = lazy(
  () => import('../layouts/company-page-layout/company-page-layout')
);
const EnrichmentPageLayout = lazy(
  () =>
    import(
      '@aibees/elephant/layouts/enrichment-page-layout/enrichment-page-layout'
    )
);
const PeoplePageLayout = lazy(
  () => import('../layouts/people-page-layout/people-page-layout')
);
const PricingLayout = lazy(
  () => import('../layouts/pricing-layout/pricing-layout')
);
const HomePageLayout = lazy(
  () => import('../layouts/home-page-layout/home-page-layout')
);
// const BuyingTeamLayout = lazy(
//   () =>
//     import(
//       /* vitePrefetch: true */ '../layouts/buying-team-layout/buying-team-layout'
//     )
// );
const SearchLayout = lazy(
  () =>
    import(/* vitePrefetch: true */ '../layouts/search-layout/search-layout')
);
const SearchListLayout = lazy(
  () => import('../layouts/search-lists-layout/search-lists-layout')
);
const SettingLayout = lazy(
  () =>
    import(/* vitePrefetch: true */ '../layouts/setting-layout/setting-layout')
);
const ExportSearchesView = lazy(() =>
  import('../views/exports-view').then((module) => ({
    default: module.default,
  }))
);
const ExportsLegacyView = lazy(() =>
  import('../views/exports-legacy-view').then((module) => ({
    default: module.ExportsLegacyView,
  }))
);
const SavedSearchesView = lazy(() =>
  import('../views/saved-searches-view').then((module) => ({
    default: module.default,
  }))
);
const DeveloperView = lazy(() =>
  import('../views/developer-view').then((module) => ({
    default: module.DeveloperView,
  }))
);

const CompanyEmployeesView = lazy(() =>
  import('../views/company-employees-view').then((module) => ({
    default: module.CompanyEmployeesView,
  }))
);
const CompanyLookalikeView = lazy(() =>
  import('../views/company-lookalike-view').then((module) => ({
    default: module.CompanyLookalikeView,
  }))
);
const CompanyInfoView = lazy(() =>
  import('../views/company-view').then((module) => ({
    default: module.CompanyView,
  }))
);
const HomePageView = lazy(() =>
  import('../views/home-page-view').then((module) => ({
    default: module.HomePageView,
  }))
);
// const BuyingTeamView = lazy(() =>
//   import(/* vitePrefetch: true */ '../views/buying-team-view').then(
//     (module) => ({
//       default: module.BuyingTeamView,
//     })
//   )
// );
const EnrichPageView = lazy(() =>
  import('../views/enrich-page-view').then((module) => ({
    default: module.EnrichPageView,
  }))
);
const LabelPageView = lazy(() =>
  import('../views/label-page-view').then((module) => ({
    default: module.LabelPageView,
  }))
);
const ListPageView = lazy(() =>
  import('../views/list-page-view').then((module) => ({
    default: module.ListPageView,
  }))
);
const CrawlRequestPageView = lazy(() =>
  import('../views/crawl-request-page-view').then((module) => ({
    default: module.CrawlRequestPageView,
  }))
);
const IntegrationPageView = lazy(() =>
  import('../views/integration-page-view').then((module) => ({
    default: module.IntegrationPageView,
  }))
);
const PeopleInfoView = lazy(() =>
  import('../views/people-view').then((module) => ({
    default: module.PeopleView,
  }))
);
const PeopleLookalikeView = lazy(() =>
  import('../views/people-lookalike-view').then((module) => ({
    default: module.PeopleLookalikeView,
  }))
);
const PeoplePersonalityAnalysis = lazy(() =>
  import('../views/people-personality-analysis').then((module) => ({
    default: module.PeoplePersonalityAnalysis,
  }))
);
const WhiteLabelView = lazy(() =>
  import('../views/white-label-view').then((module) => ({
    default: module.WhiteLabelView,
  }))
);
const DashboardSettingsView = lazy(() =>
  import('@aibees/elephant/views/backoffice/dashboard-settings-view').then(
    (module) => ({
      default: module.DashboardSettingsView,
    })
  )
);
const AccountManagementView = lazy(() =>
  import('../views/backoffice/account-management-view').then((module) => ({
    default: module.AccountManagementView,
  }))
);
const AccountStatsView = lazy(() =>
  import('../views/account-stats-view').then((module) => ({
    default: module.AccountStatsView,
  }))
);

const ChartPreviewView = lazy(() =>
  import('../views/chart-preview-view').then((module) => ({
    default: module.ChartPreviewView,
  }))
);

const PersonUpdatesView = lazy(() =>
  import('../views/person-updates-view').then((module) => ({
    default: module.PersonUpdatesView,
  }))
);

const ApiPanelDashboardView = lazy(() =>
  import('../views/api-panel-dashboard-view').then((module) => ({
    default: module.ApiPanelDashboardView,
  }))
);

const ApiPanelApiKeysView = lazy(() =>
  import('../views/api-panel-api-keys-view').then((module) => ({
    default: module.ApiPanelApiKeysView,
  }))
);

const FinancialStatsView = lazy(() =>
  import('../views/financial-stats-view').then((module) => ({
    default: module.FinancialStatsView,
  }))
);

const SearchView = lazy(
  () => import(/* vitePrefetch: true */ '../views/search-view/search-view')
);

// Lazy-loaded shared views (reduces initial bundle)
const LoginView = lazy(() =>
  import(/* vitePrefetch: true */ '@aibees/shared/views').then((m) => ({
    default: m.LoginView,
  }))
);
const LinkView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({ default: m.LinkView }))
);
const FileView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({ default: m.FileView }))
);
const WorkspaceDeleteSuccessView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({
    default: m.WorkspaceDeleteSuccessView,
  }))
);
const PriceAndPlanView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({ default: m.PriceAndPlanView }))
);
const BillingStatus = lazy(() =>
  import(
    '@aibees/shared/features/price-and-plan/components/billing-status'
  ).then((m) => ({ default: m.BillingStatus }))
);
const BillingView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({ default: m.BillingView }))
);
const CreditUsageView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({
    default: m.CreditUsageView,
  }))
);
const CurrentPlanView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({
    default: m.CurrentPlanView,
  }))
);
const UserProfileView = lazy(() =>
  import(/* vitePrefetch: true */ '@aibees/shared/views').then((m) => ({
    default: m.UserProfileView,
  }))
);
const UserManagementView = lazy(() =>
  import('@aibees/shared/views').then((m) => ({
    default: m.UserManagementView,
  }))
);

const ArkErrorDialog = lazy(() =>
  import('../components/ark-error-dialog').then((module) => ({
    default: module.ArkErrorDialog,
  }))
);

export const Router = () =>
  useRoutes([
    {
      path: R.signup,
      element: <RedirectToSignup />,
    },
    {
      path: R.workspaceDeleted,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <WorkspaceDeleteSuccessView />
        </Suspense>
      ),
    },
    {
      path: R.auth,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <LoginView />
        </Suspense>
      ),
    },
    {
      path: R.link,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <LinkView />
        </Suspense>
      ),
    },
    {
      path: R.file,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <FileView />
        </Suspense>
      ),
    },
    {
      path: R.searchListExportLegacy,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <ExportsLegacyView />
        </Suspense>
      ),
    },
    {
      path: R.root,
      element: (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthConsumer>
            <WorkspaceProvider>
              <ActionButtonContextProvider>
                <TableActionProvider>
                  <SocketProvider url={SOCKET_BASE_URL}>
                    <FullScreenLoadingProvider>
                      <InsufficientCreditsDialog />
                      <SearchLimitAlertDialog />
                      <FullScreenLoading />
                      <PostHogIdentify />
                      <ArkErrorDialog />
                      <Outlet />
                    </FullScreenLoadingProvider>
                  </SocketProvider>
                </TableActionProvider>
              </ActionButtonContextProvider>
            </WorkspaceProvider>
          </AuthConsumer>
        </LocalizationProvider>
      ),
      errorElement: <View404 />,
      children: [
        {
          path: '',
          element: <Navigate to={R.home} replace />,
        },
        {
          path: R.peopleRoot,
          element: <Navigate to={R.searchPeople} replace />,
        },
        {
          path: R.dashboard,
          element: <Navigate to={R.home} replace />,
        },
        {
          path: R.searchCompany,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <SearchLayout>
                <SearchView />
              </SearchLayout>
            </Suspense>
          ),
        },
        {
          path: R.searchPeople,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <SearchLayout>
                <SearchView />
              </SearchLayout>
            </Suspense>
          ),
        },

        /* -----------------search-list--------------------- */
        {
          path: R.searchList,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <SearchListLayout>
                <Outlet />
              </SearchListLayout>
            </Suspense>
          ),
          children: [
            // {
            //   path: '',
            //   element: <Navigate to={R.searchListSavedCompany} replace />,
            // },
            // {
            //   path: R.searchListSavedCompany,
            //   element: <SavedSearchesView />,
            // },
            // {
            //   path: R.searchListSavedPeople,
            //   element: <SavedSearchesView />,
            // },
          ],
        },
        /* -----------------pricing--------------------- */
        {
          path: R.priceAndPlan,
          element: (
            <Suspense fallback={<SplashScreen />}>
              <PricingLayout>
                <PriceAndPlanView />
              </PricingLayout>
            </Suspense>
          ),
        },

        /* -----------------payments callback--------------------- */
        {
          path: R.billingStatusCancel,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <PricingLayout>
                <BillingStatus />
              </PricingLayout>
            </Suspense>
          ),
        },
        {
          path: R.billingStatusSuccess,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <PricingLayout>
                <BillingStatus />
              </PricingLayout>
            </Suspense>
          ),
        },
        /* -----------------settings--------------------- */
        {
          path: R.settings,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <SettingLayout>
                <Outlet />
              </SettingLayout>
            </Suspense>
          ),
          children: [
            {
              path: R.apiManagementRoot,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.apiManagementDashboard} replace />,
                },
                {
                  path: R.apiManagementDashboard,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <ApiPanelDashboardView />
                    </Suspense>
                  ),
                },
                {
                  path: R.apiManagementApiKeys,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <ApiPanelApiKeysView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: R.plugInPage,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.integrationPage} replace />,
                },
                {
                  path: R.integrationPage,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <IntegrationPageView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: R.backofficeRoot,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.backofficeAccountStats} replace />,
                },
                {
                  path: R.backofficeFinancialStats,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <FinancialStatsView />
                    </Suspense>
                  ),
                },
                {
                  path: R.backofficeAccountStats,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <AccountStatsView />
                    </Suspense>
                  ),
                },
                {
                  path: R.backofficeAccountManagement,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <AccountManagementView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: R.recordsRoot,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.searchListSaved} replace />,
                },
                {
                  path: R.listPage,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <ListPageView />
                    </Suspense>
                  ),
                },
                {
                  path: R.labelPage,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <LabelPageView />
                    </Suspense>
                  ),
                },
                {
                  path: R.searchListSaved,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <SavedSearchesView />
                    </Suspense>
                  ),
                },
                {
                  path: R.searchListExport,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <ExportSearchesView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: R.planUsageHub,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.priceAndPlan} replace />,
                },
                {
                  path: R.currentPlan,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <CurrentPlanView />
                    </Suspense>
                  ),
                },
                {
                  path: R.creditUsage,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <CreditUsageView />
                    </Suspense>
                  ),
                },
                {
                  path: R.billing,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <BillingView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: R.profile,
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.userProfile} replace />,
                },
                {
                  path: R.userProfile,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <UserProfileView />
                    </Suspense>
                  ),
                },
                {
                  path: R.teamManagement,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <UserManagementView />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: '',
              element: <Navigate to={R.userProfile} replace />,
            },

            {
              path: `${R.searchListExport}/:id?`,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <ExportSearchesView />
                </Suspense>
              ),
            },
            {
              path: `${R.crawlRequestPage}/:id?`,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <CrawlRequestPageView />
                </Suspense>
              ),
            },
            {
              path: R.whiteLabelSettings,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <WhiteLabelView />
                </Suspense>
              ),
            },
            {
              path: R.developer,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <ProtectedRoute
                    realmRoles={[RealmAccessRole.DevelopmentMode]}
                  >
                    <DeveloperView />
                  </ProtectedRoute>
                </Suspense>
              ),
            },

            /* -----------------Dashboard  Settings--------------------- */
            {
              path: R.dashboardSetting,
              children: [
                {
                  path: '',
                  element: <Navigate to={R.accountStats} replace />,
                },
                {
                  path: R.accountStats,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <DashboardSettingsView />
                    </Suspense>
                  ),
                },
                {
                  path: R.financialStats,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <DashboardSettingsView />
                    </Suspense>
                  ),
                },
                {
                  path: R.worldMap,
                  element: (
                    <Suspense fallback={<LoadingScreen />}>
                      <DashboardSettingsView />
                    </Suspense>
                  ),
                },
              ],
            },

            /* -----------------Account Management Setting--------------------- */
            {
              path: R.accountManagementSetting,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <AccountManagementView />
                </Suspense>
              ),
            },
          ],
        },

        /* -----------------home--------------------- */
        {
          path: R.home,
          element: (
            <Suspense fallback={<SplashScreen />}>
              <HomePageLayout>
                <Outlet />
              </HomePageLayout>
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <HomePageView />
                </Suspense>
              ),
            },
          ],
        },

        /* -----------------buyingTeam--------------------- */
        // {
        //   path: R.buyingTeamPage,
        //   element: (
        //     <Suspense fallback={<LoadingScreen />}>
        //       <BuyingTeamLayout>
        //         <Outlet />
        //       </BuyingTeamLayout>
        //     </Suspense>
        //   ),
        //   children: [
        //     {
        //       path: '',
        //       element: (
        //         <Suspense fallback={<LoadingScreen />}>
        //           <BuyingTeamView />
        //         </Suspense>
        //       ),
        //     },
        //   ],
        // },

        /* -----------------list--------------------- */

        /* -----------------crawl request page--------------------- */

        /* -----------------Integration page--------------------- */

        /* -----------------tag--------------------- */

        /* -----------------enrich--------------------- */
        {
          path: R.enrichPage,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <EnrichmentPageLayout>
                <Outlet />
              </EnrichmentPageLayout>
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <EnrichPageView />
                </Suspense>
              ),
            },
            {
              path: ':id',
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <EnrichPageView />
                </Suspense>
              ),
            },
          ],
        },

        /* -----------------api management--------------------- */
        {
          path: R.apiManagementRoot,
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <SearchListLayout>
                <Outlet />
              </SearchListLayout>
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <Navigate to={R.apiManagementDashboard} replace />,
            },
          ],
        },

        /* -----------------company info--------------------- */
        {
          path: R.companyRoot,
          element: (
            <Suspense fallback={<SplashScreen />}>
              <CompanyPageLayout>
                <Outlet />
              </CompanyPageLayout>
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <Navigate to={R.searchCompany} replace />,
            },
            {
              path: R.company,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <CompanyInfoView />
                </Suspense>
              ),
            },
            {
              path: R.companyEmployees,
              element: (
                <SearchTableScopeRouteProvider>
                  <SearchParamsUrlSync>
                    {/* Applies `?export-search=<id>` when a "View Search" link
                        sends an employees-table export back to this page. */}
                    <ExportSearchLoader />
                    <SearchParamsSourceProvider>
                      <Suspense fallback={<LoadingScreen />}>
                        <CompanyEmployeesView />
                      </Suspense>
                    </SearchParamsSourceProvider>
                  </SearchParamsUrlSync>
                </SearchTableScopeRouteProvider>
              ),
            },
            {
              path: R.companyLookalike,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <CompanyLookalikeView />
                </Suspense>
              ),
            },
          ],
        },

        /* -----------------people info--------------------- */
        {
          path: R.peopleRoot,
          element: (
            <Suspense fallback={<SplashScreen />}>
              <PeoplePageLayout>
                <Outlet />
              </PeoplePageLayout>
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <Navigate to={R.searchPeople} replace />,
            },
            {
              path: R.people,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <PeopleInfoView />
                </Suspense>
              ),
            },
            {
              path: R.peoplePersonalityAnalysis,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <PeoplePersonalityAnalysis />
                </Suspense>
              ),
            },
            {
              path: R.peopleUpdates,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <PersonUpdatesView />
                </Suspense>
              ),
              children: [
                {
                  path: R.peopleUpdatesProfile,
                  element: <PersonUpdatesView />,
                },
                {
                  path: R.peopleUpdatesContact,
                  element: <PersonUpdatesView />,
                },
                {
                  path: R.peopleUpdatesAnalysis,
                  element: <PersonUpdatesView />,
                },
              ],
            },
            {
              path: R.peopleLookalike,
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <PeopleLookalikeView />
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    /* -----------------charts--------------------- */
    {
      path: R.chart,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <ChartPreviewView />
        </Suspense>
      ),
    },

    /* -----------------error--------------------- */
    {
      path: '*',
      element: <View404 />,
    },
  ]);
